// client/src/pages/ResourceDetail.tsx

import React, { useState, useEffect, useMemo, memo } from 'react';
import { useParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import {
    ArrowLeft,
    Calendar,
    Download,
    ExternalLink,
    FileText,
    Link as LinkIcon,
    Star,
    Tag,
} from 'lucide-react';
import {
    Resource,
    ResourceCategoryLabels,
    ResourceParentCategoryLabels,
} from '../shared/models/Resource';
import './ResourceDetail.css';

// ============================================
// CLOUDINARY HELPERS
// ============================================

/**
 * Generate an optimized Cloudinary image URL.
 *
 * This is used for image assets only.
 */
const getOptimizedImageUrl = (
    url: string,
    options?: {
        width?: number;
        height?: number;
        quality?: string;
    }
) => {
    if (!url || !url.includes('cloudinary')) {
        return url;
    }

    const {
        width = 800,
        height = 600,
        quality = 'auto',
    } = options || {};

    return url.replace(
        '/upload/',
        `/upload/w_${width},h_${height},c_fill,q_${quality}/`
    );
};

/**
 * Convert a Cloudinary image/upload URL into a forced-download URL.
 *
 * Cloudinary's fl_attachment delivery flag tells the browser to
 * download the file rather than display it inline.
 *
 * The original URL stored in MongoDB is NOT modified.
 */
const getCloudinaryDownloadUrl = (url: string): string => {
    if (!url || !url.includes('res.cloudinary.com')) {
        return url;
    }

    // Already configured for attachment delivery.
    if (url.includes('/image/upload/fl_attachment/')) {
        return url;
    }

    // Main PDF/document delivery path.
    if (url.includes('/image/upload/')) {
        return url.replace(
            '/image/upload/',
            '/image/upload/fl_attachment/'
        );
    }

    // If the URL isn't a standard Cloudinary image upload URL,
    // return it unchanged rather than making an unsafe assumption.
    return url;
};

// ============================================
// COMPONENT
// ============================================

const ResourceDetail: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();

    const [resource, setResource] = useState<Resource | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // ============================================
    // FETCH RESOURCE
    // ============================================

    useEffect(() => {
        const fetchResource = async () => {
            if (!slug) {
                setError('Resource not found');
                setLoading(false);
                return;
            }

            // Check cache first
            const cacheKey = `resource_${slug}`;
            const cachedResource = sessionStorage.getItem(cacheKey);

            if (cachedResource) {
                try {
                    setResource(JSON.parse(cachedResource));
                    setLoading(false);
                    return;
                } catch {
                    // Invalid cached data — continue with API request.
                    sessionStorage.removeItem(cacheKey);
                }
            }

            setLoading(true);
            setError('');

            try {
                const response = await fetch(
                    `/.netlify/functions/public-resource?slug=${encodeURIComponent(slug)}`
                );

                if (!response.ok) {
                    if (response.status === 404) {
                        setError(
                            'The resource you are looking for does not exist.'
                        );
                    } else {
                        throw new Error(
                            'Failed to fetch resource. Please try again later.'
                        );
                    }

                    setLoading(false);
                    return;
                }

                const data = await response.json();

                // Only show published resources to the public.
                if (
                    data.data.status !== 'published' ||
                    data.data.isPublic !== true
                ) {
                    setError(
                        'This resource is not currently available to the public.'
                    );
                    setLoading(false);
                    return;
                }

                // Cache the resource.
                sessionStorage.setItem(
                    cacheKey,
                    JSON.stringify(data.data)
                );

                setResource(data.data);
                setError('');
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : 'Failed to load resource. Please check your connection and try again.'
                );

                console.error('Error fetching resource:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchResource();
    }, [slug]);

    // ============================================
    // CATEGORY MAPPING
    // ============================================

    const parentCategoryMapping = useMemo(
        () =>
            ({
                publication_position_statement: 'publications',
                publication_policy_brief: 'publications',
                publication_research_paper: 'publications',
                publication_annual_report: 'publications',
                publication_case_study: 'publications',

                data_statistical_report: 'data',
                data_snapshot: 'data',
                data_survey_findings: 'data',
                data_interactive: 'data',

                reference_constitution: 'reference',
                reference_policy_document: 'reference',
                reference_research_database: 'reference',
                reference_partner_publication: 'reference',

                media_video: 'media',
                media_podcast: 'media',
                media_infographic: 'media',
                media_photo_gallery: 'media',

                tool_advocacy_kit: 'tools',
                tool_training_material: 'tools',
                tool_community_guide: 'tools',
                tool_how_to: 'tools',

                press_release: 'press',
                press_media_statement: 'press',
                press_interview: 'press',
                press_media_contact: 'press',
            }) as Record<string, string>,
        []
    );

    // ============================================
    // HELPERS
    // ============================================

    /**
     * Get parent category for breadcrumb navigation.
     */
    const getParentCategory = (category: string): string => {
        return parentCategoryMapping[category] || '';
    };

    // ============================================
    // LOADING STATE
    // ============================================

    if (loading) {
        return (
            <div className="resource-detail-container">
                <div className="loading-spinner">
                    <div className="spinner-animation"></div>
                    <p>Loading resource...</p>
                </div>
            </div>
        );
    }

    // ============================================
    // ERROR STATE
    // ============================================

    if (error || !resource) {
        return (
            <div className="resource-detail-container">
                <div className="error-container">
                    <h2>Resource Not Available</h2>

                    <p>
                        {error ||
                            'The resource you are looking for does not exist.'}
                    </p>

                    <div className="error-actions">
                        <Link
                            to="/resources"
                            className="btn-back"
                        >
                            <ArrowLeft
                                size={18}
                                aria-hidden="true"
                            />
                            <span>Back to Resources</span>
                        </Link>

                        <a
                            href="/contact"
                            className="btn-contact"
                        >
                            <LinkIcon
                                size={18}
                                aria-hidden="true"
                            />
                            <span>Report an Issue</span>
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    // ============================================
    // DERIVED DATA
    // ============================================

    const parentCategory = getParentCategory(resource.category);

    const parentLabel = parentCategory
        ? ResourceParentCategoryLabels[parentCategory]
        : '';

    const downloadUrl = resource.mainFile
        ? getCloudinaryDownloadUrl(resource.mainFile.url)
        : '';

    const renderedBodyHtml = useMemo(() => {
        const canonicalHtmlRaw =
            typeof resource.contentHtmlSanitized ===
            'string'
                ? resource.contentHtmlSanitized
                : '';

        const sanitizedCanonicalHtml =
            canonicalHtmlRaw
                ? DOMPurify.sanitize(
                      canonicalHtmlRaw,
                      {
                          USE_PROFILES: {
                              html: true
                          }
                      }
                  ).trim()
                : '';

        const hasValidCanonical =
            resource.hasCanonicalContent === true &&
            sanitizedCanonicalHtml.length > 0;

        if (hasValidCanonical) {
            return sanitizedCanonicalHtml;
        }

        const legacyRawContent =
            typeof resource.content === 'string'
                ? resource.content
                : '';

        if (!legacyRawContent) {
            return '';
        }

        return DOMPurify.sanitize(
            legacyRawContent,
            {
                USE_PROFILES: {
                    html: true
                }
            }
        ).trim();
    }, [
        resource.content,
        resource.contentHtmlSanitized,
        resource.hasCanonicalContent,
    ]);

    // ============================================
    // RENDER
    // ============================================

    return (
        <div className="resource-detail-container">
            {/* ========================================
                BREADCRUMB
            ======================================== */}

            <nav
                className="breadcrumb"
                aria-label="Breadcrumb"
            >
                <Link to="/">
                    Home
                </Link>

                <span
                    className="separator"
                    aria-hidden="true"
                >
                    ›
                </span>

                <Link to="/resources">
                    Resources
                </Link>

                {parentLabel && (
                    <>
                        <span
                            className="separator"
                            aria-hidden="true"
                        >
                            ›
                        </span>

                        <span className="breadcrumb-parent">
                            {parentLabel}
                        </span>
                    </>
                )}

                <span
                    className="separator"
                    aria-hidden="true"
                >
                    ›
                </span>

                <span className="breadcrumb-current">
                    {resource.title}
                </span>
            </nav>

            {/* ========================================
                RESOURCE HEADER
            ======================================== */}

            <header className="resource-header">
                <div className="resource-meta-top">
                    <span className="resource-category-badge">
                        {ResourceCategoryLabels[resource.category]}
                    </span>

                    {resource.isFeatured && (
                        <span className="featured-badge">
                            <Star
                                size={15}
                                aria-hidden="true"
                            />
                            <span>Featured</span>
                        </span>
                    )}

                    <span className="resource-date">
                        <Calendar
                            size={15}
                            aria-hidden="true"
                        />

                        <span>
                            {new Date(
                                resource.publishedDate
                            ).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                            })}
                        </span>
                    </span>
                </div>

                <h1>{resource.title}</h1>

                {resource.authors &&
                    resource.authors.length > 0 && (
                        <div className="resource-authors">
                            {resource.authors.map(
                                (author, index) => (
                                    <span key={index}>
                                        {author.name}

                                        {author.title &&
                                            `, ${author.title}`}

                                        {author.affiliation &&
                                            ` (${author.affiliation})`}

                                        {index <
                                            resource.authors.length -
                                                1 && '; '}
                                    </span>
                                )
                            )}
                        </div>
                    )}
            </header>

            {/* ========================================
                RESOURCE CONTENT
            ======================================== */}

            <div className="resource-body">
                {/* Description */}
                <section className="resource-description-section">
                    <h2>About This Resource</h2>

                    <p className="resource-full-description">
                        {resource.description}
                    </p>
                </section>

                {/* Abstract */}
                {resource.abstract && (
                    <section className="resource-abstract-section">
                        <h2>Abstract</h2>

                        <p>{resource.abstract}</p>
                    </section>
                )}

                {/* Key Findings */}
                {resource.keyFindings &&
                    resource.keyFindings.length > 0 && (
                        <section className="resource-key-findings">
                            <h2>Key Findings</h2>

                            <ul>
                                {resource.keyFindings.map(
                                    (finding, index) => (
                                        <li key={index}>
                                            {finding}
                                        </li>
                                    )
                                )}
                            </ul>
                        </section>
                    )}

                {/* Full Content */}
                {renderedBodyHtml && (
                    <section className="resource-content-section">
                        <h2>Full Content</h2>

                        <div
                            dangerouslySetInnerHTML={{
                                __html: renderedBodyHtml,
                            }}
                        />
                    </section>
                )}

                {/* Tags */}
                {resource.tags &&
                    resource.tags.length > 0 && (
                        <section className="resource-tags">
                            <h3>
                                <Tag
                                    size={17}
                                    aria-hidden="true"
                                />
                                <span>Tags</span>
                            </h3>

                            <div className="tags-list">
                                {resource.tags.map(
                                    (tag, index) => (
                                        <span
                                            key={index}
                                            className="tag"
                                        >
                                            #{tag}
                                        </span>
                                    )
                                )}
                            </div>
                        </section>
                    )}

                {/* ========================================
                    ACTIONS
                ======================================== */}

                <section className="resource-actions-section">
                    {/* Download */}
                    {resource.mainFile && (
                        <a
                            href={downloadUrl}
                            className="btn-download-large"
                            aria-label={`Download ${resource.mainFile.filename}`}
                        >
                            <Download
                                size={19}
                                aria-hidden="true"
                            />

                            <span>
                                Download{' '}
                                {resource.mainFile.filename}
                            </span>

                            {resource.mainFile.size && (
                                <span className="file-size">
                                    (
                                    {Math.round(
                                        resource.mainFile.size /
                                            1024
                                    )}{' '}
                                    KB)
                                </span>
                            )}
                        </a>
                    )}

                    {/* External Resource */}
                    {resource.externalUrl && (
                        <a
                            href={resource.externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-external"
                        >
                            <ExternalLink
                                size={18}
                                aria-hidden="true"
                            />

                            <span>
                                View External Resource
                            </span>
                        </a>
                    )}

                    {/* DOI */}
                    {resource.doi && (
                        <div className="doi-info">
                            <strong>DOI:</strong>{' '}

                            <a
                                href={`https://doi.org/${resource.doi}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {resource.doi}
                            </a>
                        </div>
                    )}

                    {/* Recommended Citation */}
                    {resource.recommendedCitation && (
                        <div className="citation-info">
                            <strong>
                                Recommended Citation:
                            </strong>

                            <p>
                                {resource.recommendedCitation}
                            </p>
                        </div>
                    )}
                </section>

                {/* ========================================
                    BACK TO RESOURCES
                ======================================== */}

                <div className="back-to-resources">
                    <Link
                        to="/resources"
                        className="back-link"
                    >
                        <ArrowLeft
                            size={18}
                            aria-hidden="true"
                        />

                        <span>Back to Resources</span>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default memo(ResourceDetail);